<template>
  <div class="notification-record-page">
    <el-card shadow="never">
      <div slot="header" class="card-title">邮件记录</div>

      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="收件人">
          <el-input
            v-model.trim="searchForm.receiver"
            clearable
            maxlength="100"
            placeholder="邮箱地址"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item label="时间">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            value-format="timestamp"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
          />
        </el-form-item>
        <el-form-item label="邮件主题">
          <el-input
            v-model.trim="searchForm.subject"
            clearable
            maxlength="100"
            placeholder="邮件主题"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item label="邮件正文">
          <el-input
            v-model.trim="searchForm.content"
            clearable
            maxlength="100"
            placeholder="邮件正文"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>
        <el-form-item label="发送成功">
          <el-select
            v-model="searchForm.isSuccess"
            clearable
            placeholder="请选择"
          >
            <el-option label="是" value="true" />
            <el-option label="否" value="false" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button icon="el-icon-refresh" @click="handleReset"
            >重置</el-button
          >
          <el-button
            type="success"
            icon="el-icon-download"
            :loading="exporting"
            @click="handleExport"
          >
            导出Excel
          </el-button>
        </el-form-item>
      </el-form>

      <el-table v-loading="loading" :data="tableData" border stripe>
        <el-table-column type="index" label="序号" width="70" align="center" />
        <el-table-column
          prop="receiver"
          label="收件人邮箱地址"
          min-width="190"
          show-overflow-tooltip
        />
        <el-table-column
          prop="subject"
          label="邮件主题"
          min-width="200"
          show-overflow-tooltip
        />
        <el-table-column
          prop="content"
          label="邮件正文"
          min-width="320"
          show-overflow-tooltip
        />
        <el-table-column
          prop="attachment"
          label="邮件附件路径"
          min-width="220"
          show-overflow-tooltip
        />
        <el-table-column label="是否发送成功" width="130" align="center">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.success ? 'success' : 'danger'"
              size="small"
            >
              {{ formatSuccess(scope.row.success) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="created_at"
          label="创建时间"
          width="180"
          align="center"
          :formatter="formatTableDate"
        />
      </el-table>

      <div class="pagination-wrapper">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          :page-sizes="[10, 15, 20, 25]"
          :page-size="pagination.pageSize"
          :current-page="pagination.currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import ExportJsonExcel from "js-export-excel";
import { getEmailRecordList } from "@/api/notification";
import { formatDate } from "@/utils/formatter";

const createSearchForm = () => ({
  receiver: "",
  dateRange: [],
  subject: "",
  content: "",
  isSuccess: ""
});

export default {
  name: "EmailRecords",
  data() {
    return {
      loading: false,
      exporting: false,
      tableData: [],
      searchForm: createSearchForm(),
      pagination: {
        currentPage: 1,
        pageSize: 15,
        total: 0
      }
    };
  },
  created() {
    this.fetchData();
  },
  methods: {
    buildParams(withPagination = true) {
      const params = {};
      if (this.searchForm.receiver) {
        params.receiver = this.searchForm.receiver;
      }
      if (
        this.searchForm.dateRange &&
        this.searchForm.dateRange.length === 2
      ) {
        params.begin_date = Math.floor(this.searchForm.dateRange[0] / 1000);
        params.end_date =
          Math.floor(this.searchForm.dateRange[1] / 1000) + 24 * 60 * 60 - 1;
      }
      if (this.searchForm.subject) {
        params.subject = this.searchForm.subject;
      }
      if (this.searchForm.content) {
        params.content = this.searchForm.content;
      }
      if (this.searchForm.isSuccess !== "") {
        params.is_success = this.searchForm.isSuccess;
      }
      if (withPagination) {
        params.page = this.pagination.currentPage;
        params.rows = this.pagination.pageSize;
      }
      return params;
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await getEmailRecordList(this.buildParams());
        this.tableData = (res && res.rows) || [];
        this.pagination.total = Number((res && res.total) || 0);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    handleReset() {
      this.searchForm = createSearchForm();
      this.pagination.currentPage = 1;
      this.pagination.pageSize = 15;
      this.fetchData();
    },
    handleSizeChange(pageSize) {
      this.pagination.pageSize = pageSize;
      this.pagination.currentPage = 1;
      this.fetchData();
    },
    handleCurrentChange(currentPage) {
      this.pagination.currentPage = currentPage;
      this.fetchData();
    },
    async handleExport() {
      this.exporting = true;
      try {
        const res = await getEmailRecordList(this.buildParams(false));
        const rows = (res && res.rows) || [];
        if (!rows.length) {
          this.$message.warning("没有可导出的邮件记录");
          return;
        }
        const sheetData = rows.map(item => ({
          收件人邮箱地址: item.receiver || "",
          邮件主题: item.subject || "",
          邮件正文: item.content || "",
          邮件附件路径: item.attachment || "",
          是否发送成功: this.formatSuccess(item.success),
          创建时间: this.formatDateValue(item.created_at)
        }));
        new ExportJsonExcel({
          fileName: "邮件记录",
          datas: [
            {
              sheetData,
              sheetName: "邮件记录",
              sheetHeader: [
                "收件人邮箱地址",
                "邮件主题",
                "邮件正文",
                "邮件附件路径",
                "是否发送成功",
                "创建时间"
              ]
            }
          ]
        }).saveExcel();
      } finally {
        this.exporting = false;
      }
    },
    formatSuccess(value) {
      return value ? "成功" : "失败";
    },
    formatTableDate(row, column, cellValue) {
      return this.formatDateValue(cellValue);
    },
    formatDateValue(value) {
      if (!value) return "";
      const normalized =
        typeof value === "number" && value < 1000000000000
          ? value * 1000
          : value;
      return formatDate(normalized);
    }
  }
};
</script>

<style lang="less" scoped>
.notification-record-page {
  padding: 20px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
}

.search-form {
  margin-bottom: 4px;
}

.search-type {
  width: 170px;
}

.pagination-wrapper {
  margin-top: 16px;
  text-align: right;
}
</style>
